import { Link } from "react-router-dom";
import { Lock, ArrowUpRight } from "lucide-react";
import { analytics } from "@/lib/analytics";

const SISTER_SITES = [
  {
    id: "strata",
    name: "Strata",
    blurb: "The whole AI stack, explained simply — every number sourced.",
    href: "https://strata.dheeco.com",
  },
  {
    id: "apfactcheck",
    name: "AP Fact Check",
    blurb: "Andhra Pradesh projects and budgets, every figure from a government document.",
    href: "https://apfactcheck.dheeco.com",
  },
  {
    id: "dheeco-home",
    name: "dheeco.com",
    blurb: "Everything else we're building.",
    href: "https://dheeco.com",
  },
];

const LINKS = [
  { label: "Community",  href: "#community-board" },
  { label: "Events",     href: "#events" },
  { label: "Housing",    href: "#housing" },
  { label: "Jobs",       href: "#jobs" },
  { label: "Businesses", href: "#business-directory" },
  { label: "Feedback",   href: "#feedback" },
];


export function Footer() {
  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer className="bg-[#f5f5f7] border-t border-[#d2d2d7]">
      <div className="max-w-[980px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* Main grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 py-14 border-b border-[#d2d2d7]">

          {/* Brand */}
          <div>
            <p
              className="text-[21px] font-semibold text-[#1d1d1f]"
              style={{ letterSpacing: "-0.02em" }}
            >
              NC Desi
            </p>
            <p className="mt-1 text-[13px] text-[#6e6e73]">
              built by{" "}
              <a
                href="https://dheeco.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.clickOutbound("dheeco-byline")}
                className="font-medium text-[#1d1d1f] hover:text-[#0071e3] transition-colors duration-150"
              >
                dheeco
              </a>
            </p>
            <p className="mt-3 text-[14px] leading-relaxed text-[#6e6e73]">
              Fostering connections and culture for the Indian community across North Carolina. Built by the community. For the community.
            </p>
            <p className="mt-4 text-[15px] italic text-[#0071e3]">
              "There was no NC Desi before. Now there is."
            </p>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-[12px] font-semibold tracking-[0.12em] uppercase text-[#6e6e73] mb-4">
              Explore
            </h4>
            <ul className="space-y-2.5">
              {LINKS.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => go(l.href)}
                    className="text-[14px] text-[#1d1d1f] hover:text-[#0071e3] transition-colors duration-150"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Admin */}
          <div>
            <h4 className="text-[12px] font-semibold tracking-[0.12em] uppercase text-[#6e6e73] mb-4">
              Legal
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a href="/privacy" className="text-[14px] text-[#1d1d1f] hover:text-[#0071e3] transition-colors duration-150">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/terms" className="text-[14px] text-[#1d1d1f] hover:text-[#0071e3] transition-colors duration-150">
                  Terms of Use
                </a>
              </li>
            </ul>
            <Link
              to="/admin"
              className="mt-5 inline-flex items-center gap-2 text-[13px] text-[#6e6e73] hover:text-[#0071e3] transition-colors"
            >
              <Lock className="w-3.5 h-3.5" /> Admin Portal
            </Link>
          </div>

          {/* More from dheeco */}
          <div>
            <h4 className="text-[12px] font-semibold tracking-[0.12em] uppercase text-[#6e6e73] mb-4">
              More from dheeco
            </h4>
            <ul className="space-y-4">
              {SISTER_SITES.map((s) => (
                <li key={s.href}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => analytics.clickOutbound(s.id)}
                    className="group block"
                  >
                    <span className="flex items-center gap-1.5 text-[14px] text-[#1d1d1f] group-hover:text-[#0071e3] transition-colors duration-150">
                      {s.name}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                    <span className="block mt-0.5 text-[12px] leading-snug text-[#6e6e73]">
                      {s.blurb}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[13px] text-[#6e6e73]">
          <p>© {new Date().getFullYear()} NC Desi, built by dheeco. North Carolina's Desi Home.</p>
          <p className="flex items-center gap-2">
            <span>Made with love by the community</span>
            <span className="text-[#d2d2d7]" aria-hidden="true">·</span>
            <a
              href="https://dheeco.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analytics.clickOutbound("dheeco-footer-url")}
              className="text-[#1d1d1f] hover:text-[#0071e3] transition-colors duration-150"
            >
              dheeco.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
